import React, { useEffect, useState, useCallback } from 'react';
import { Eye, TrendingUp, TrendingDown, AlertTriangle, Shield, Clock } from 'lucide-react';
import { cn } from '../lib/utils';

interface WatchRow {
  symbol: string;
  first_price: number | null;
  last_price: number | null;
  highest_price: number | null;
  tracked_high: number | null;
  max_move_pct: number | null;
  latest_integrity_band: string | null;
  latest_integrity_decision_band: string | null;
  latest_integrity_status: string | null;
  latest_red_flag_components: string | null;
  latest_catalyst_count: number | null;
  user_holds_position: number;
  user_recently_traded: number;
  first_seen_at: number | null;
  last_seen_at: number | null;
  source_section: string | null;
}

interface ContinuationWatchTableProps {
  apiBase: string;
  onSelectSymbol?: (symbol: string) => void;
}

const BAND_COLORS: Record<string, string> = {
  CLEAR: 'text-emerald-400',
  CAUTION: 'text-amber-400',
  HIGH_RISK: 'text-rose-400',
  DO_NOT_TRADE: 'text-rose-600',
  PARTIAL_FACTS: 'text-zinc-400',
};

const PROM_BADGE: Record<string, { label: string; cls: string }> = {
  HELD_POSITION: { label: 'HELD', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  RECENTLY_TRADED: { label: 'TRADED', cls: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
  DECISION_CLEAR: { label: 'CLEAR', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  WATCH_ONLY: { label: 'WATCH', cls: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30' },
};

export function ContinuationWatchTable({ apiBase, onSelectSymbol }: ContinuationWatchTableProps) {
  const [rows, setRows] = useState<WatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWatch = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/market-pulse/continuation-watch?limit=50`);
      if (!res.ok) { setError(`HTTP ${res.status}`); return; }
      const json = await res.json();
      const data = json?.data?.rows || json?.rows || [];
      if (Array.isArray(data)) setRows(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchWatch();
    const t = setInterval(fetchWatch, 60_000);
    return () => clearInterval(t);
  }, [fetchWatch]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-6">
        <div className="flex items-center gap-2 text-[var(--muted-text)] text-xs">
          <Clock size={14} className="animate-pulse" /> Loading continuation watch...
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--card-border)] flex items-center justify-between bg-[var(--card-header-bg)]">
        <div className="flex items-center gap-2">
          <Eye size={16} className="text-amber-400" />
          <span className="text-sm font-black uppercase italic tracking-tight text-[var(--app-text)]">
            Continuation Watch
          </span>
          <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest ml-1">
            Advisory Only
          </span>
        </div>
        <span className="text-[10px] text-[var(--muted-text)]">{rows.length} active</span>
      </div>

      {rows.length === 0 ? (
        <div className="p-8 text-center text-xs text-[var(--muted-text)] opacity-50">
          No continuation watches active
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--card-border)] text-[var(--muted-text)] uppercase tracking-widest text-[10px]">
                <th className="text-left px-4 py-2.5 font-bold">Symbol</th>
                <th className="text-right px-3 py-2.5 font-bold">Move %</th>
                <th className="text-right px-3 py-2.5 font-bold">Price</th>
                <th className="text-right px-3 py-2.5 font-bold">High</th>
                <th className="text-center px-3 py-2.5 font-bold">Band</th>
                <th className="text-center px-3 py-2.5 font-bold">Prominence</th>
                <th className="text-center px-3 py-2.5 font-bold">Risk</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const move = r.max_move_pct;
                const moveUp = move !== null && move >= 0;
                const band = r.latest_integrity_decision_band || '';
                const bandCls = BAND_COLORS[band] || 'text-zinc-500';
                const holds = Boolean(r.user_holds_position);
                const traded = Boolean(r.user_recently_traded);
                const promKey = holds ? 'HELD_POSITION' : traded ? 'RECENTLY_TRADED' : (band === 'CLEAR' ? 'DECISION_CLEAR' : 'WATCH_ONLY');
                const prom = PROM_BADGE[promKey];
                const flags = r.latest_red_flag_components || '';
                return (
                  <tr
                    key={r.symbol}
                    className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)] transition-colors cursor-pointer"
                    onClick={() => onSelectSymbol?.(r.symbol)}
                  >
                    <td className="px-4 py-2.5 font-bold text-[var(--app-text)]">
                      <div className="flex items-center gap-1.5">
                        {holds && <Shield size={12} className="text-sky-400" />}
                        {r.symbol}
                      </div>
                    </td>
                    <td className={cn('text-right px-3 py-2.5 font-mono font-bold', moveUp ? 'text-emerald-400' : 'text-rose-400')}>
                      <span className="inline-flex items-center gap-0.5">
                        {moveUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {move !== null ? `${move > 0 ? '+' : ''}${move.toFixed(1)}%` : '—'}
                      </span>
                    </td>
                    <td className="text-right px-3 py-2.5 font-mono text-[var(--app-text)]">
                      {r.last_price != null ? `$${r.last_price.toFixed(2)}` : '—'}
                    </td>
                    <td className="text-right px-3 py-2.5 font-mono text-[var(--muted-text)]">
                      {r.tracked_high != null ? `$${r.tracked_high.toFixed(2)}` : '—'}
                    </td>
                    <td className={cn('text-center px-3 py-2.5 font-bold', bandCls)}>
                      {band || '—'}
                    </td>
                    <td className="text-center px-3 py-2.5">
                      <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border', prom.cls)}>
                        {prom.label}
                      </span>
                    </td>
                    <td className="text-center px-3 py-2.5">
                      {flags ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-400">
                          <AlertTriangle size={10} />
                          {flags}
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
